import { useEffect, useRef } from "react";
import JsBarcode from "jsbarcode";

// Leitores de código de barras USB/Bluetooth funcionam como teclado: escrevem o código muito depressa e terminam com Enter.
// O hook apanha essas sequências rápidas em qualquer ponto da página (fora de campos de texto) e chama onScan(code).
const MAX_GAP_MS = 60;
const MIN_LENGTH = 4;

const isEditable = (el) => !!el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable);

export const useBarcodeScanner = (onScan, { enabled = true } = {}) => {
  const buffer = useRef("");
  const last = useRef(0);
  const cb = useRef(onScan);
  cb.current = onScan;

  useEffect(() => {
    if (!enabled) return undefined;
    const onKey = (e) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (isEditable(document.activeElement) && !document.activeElement.dataset.scanTarget) return;
      const now = Date.now();
      if (now - last.current > MAX_GAP_MS) buffer.current = "";
      last.current = now;
      if (e.key === "Enter") {
        const code = buffer.current;
        buffer.current = "";
        if (code.length >= MIN_LENGTH) { e.preventDefault(); cb.current(code); }
        return;
      }
      if (e.key.length === 1) buffer.current += e.key;
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [enabled]);
};

export const normalizeBarcode = (v) => String(v || "").replace(/[\s-]/g, "").toUpperCase();

export const isValidEan13 = (code) => {
  if (!/^\d{13}$/.test(code)) return false;
  const sum = code.slice(0, 12).split("").reduce((t, d, i) => t + Number(d) * (i % 2 ? 3 : 1), 0);
  return (10 - (sum % 10)) % 10 === Number(code[12]);
};

// Som curto de confirmação (agudo) ou de erro (grave) — útil no balcão
export const beep = (ok = true) => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = beep.ctx || (beep.ctx = new Ctx());
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "square";
    osc.frequency.value = ok ? 1800 : 300;
    gain.gain.value = 0.08;
    osc.connect(gain).connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + (ok ? 0.08 : 0.25));
  } catch { /* sem áudio */ }
};

// SVG do código de barras (EAN-13 quando válido, senão CODE128) para etiquetas e fichas de produto
export const barcodeSvg = (code, { height = 50, width = 2, fontSize = 14, displayValue = true } = {}) => {
  if (!code) return "";
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  try {
    JsBarcode(svg, code, { format: isValidEan13(code) ? "EAN13" : "CODE128", height, width, fontSize, displayValue, margin: 2, flat: true });
    return svg.outerHTML;
  } catch {
    return "";
  }
};
